/**
 * OE Condition workflow rules — pure, unit-tested (Beta-1 plan P2-09..14, P2-36, P2-37).
 *
 *   - review ROUTING by the entrant's role (P2-37, Tim C0820 / TICK C45): who is L1 and L2 for a
 *     condition. Defaults below; an organization may override them in
 *     `Hugo_Organizations.reviewRouting` (Settings → Organization → Review routing).
 *   - which items sit in a viewer's L1 / L2 queue (role + scope)
 *   - who may resolve, approve a resolution, cancel, revise (P2-12, P2-36)
 *
 * A `viewer` is { uid, role, isAdmin, nwtId, deptId, siteId, orgId, memberOfDeptIds?: [] }
 * (DemoRoleContext.queryScope + the stored role key). A `condition` is a Hugo_TRLs record.
 */
import { ROLE_HIERARCHY, roleRank } from '../../../services/roleHierarchy.js';

export const REVIEWER_ROLES = Object.freeze({
  team_lead: 'team_lead', // FLS of the condition's NWT
  department_lead: 'department_lead', // DH of the department
  site_manager: 'site_manager', // Facility/Site/Plant Manager of the site
  org_admin: 'org_admin', // REPSit.ai SME / administrator
});

/**
 * Default routing by the ENTRANT's role (TICK C45): "For levels of Department Head and below,
 * approval is routed to whoever the individual reports to. For levels of Site/Facility Manager
 * and above, the approval authority should be the REPSit.ai SME/administrator."
 * `l2: null` = single-step review (L1 approval makes it Approved/Active).
 */
export const DEFAULT_REVIEW_ROUTING = Object.freeze({
  respondent: { l1: 'team_lead', l2: 'department_lead' },
  team_lead: { l1: 'department_lead', l2: 'site_manager' },
  department_lead: { l1: 'site_manager', l2: 'org_admin' },
  site_manager: { l1: 'org_admin', l2: null },
  executive: { l1: 'org_admin', l2: null },
  org_admin: { l1: 'org_admin', l2: null },
  org_it: { l1: 'org_admin', l2: null },
});

/** Demo persona id → stored role key (what the entry form records as `submitterRole`). */
export const PERSONA_TO_ROLE = Object.freeze({
  itm: 'respondent',
  fls: 'team_lead',
  dh: 'department_lead',
  site_manager: 'site_manager',
  executive: 'executive',
  admin: 'org_admin',
  ppi: 'org_admin',
  org_it: 'org_it',
});

export function routingFor(submitterRole, orgRouting = null) {
  const role = submitterRole && (DEFAULT_REVIEW_ROUTING[submitterRole] || orgRouting?.[submitterRole]) ? submitterRole : 'respondent';
  const override = orgRouting && orgRouting[role];
  const base = DEFAULT_REVIEW_ROUTING[role] || DEFAULT_REVIEW_ROUTING.respondent;
  return { l1: override?.l1 || base.l1, l2: override && 'l2' in override ? override.l2 : base.l2 };
}

/** Is this viewer, by role + scope, the L1 (or L2) authority for the condition? */
function viewerHoldsRole(reviewerRole, condition, viewer) {
  if (!reviewerRole || !viewer) return false;
  if (viewer.isAdmin) return true; // administrators can act at any hop (Tim: admin override)
  if (viewer.role !== reviewerRole) return false;
  switch (reviewerRole) {
    case 'team_lead':
      return Boolean(viewer.nwtId) && viewer.nwtId === condition.nwtId;
    case 'department_lead':
      return Boolean(viewer.deptId) && viewer.deptId === condition.deptId;
    case 'site_manager':
      return Boolean(viewer.siteId) && viewer.siteId === condition.siteId;
    case 'org_admin':
      return !viewer.orgId || !condition.organizationId || viewer.orgId === condition.organizationId;
    default:
      return false;
  }
}

export function isL1For(condition, viewer, orgRouting = null) {
  return viewerHoldsRole(routingFor(condition.submitterRole, orgRouting).l1, condition, viewer);
}

export function isL2For(condition, viewer, orgRouting = null) {
  const { l2 } = routingFor(condition.submitterRole, orgRouting);
  return Boolean(l2) && viewerHoldsRole(l2, condition, viewer);
}

/** Single-step review: L1 approval goes straight to Approved/Active (P2-37). */
export function isSingleStep(condition, orgRouting = null) {
  return routingFor(condition.submitterRole, orgRouting).l2 == null;
}

/* ------------------------------------------------------------------ queues */

const HIDDEN = new Set(['withdrawn', 'cancelled']);

export function isHiddenForViewer(condition, viewer) {
  if (!condition) return true;
  if (HIDDEN.has(condition.status) && !viewer?.isAdmin) return true;
  return Array.isArray(condition.hiddenFor) && viewer?.uid ? condition.hiddenFor.includes(viewer.uid) : false;
}

/** Awaiting THIS viewer as L1: new items, plus resolutions needing the L1 sign-off. */
export function needsL1Action(condition, viewer, orgRouting = null) {
  if (!isL1For(condition, viewer, orgRouting)) return false;
  if (condition.status === 'submitted' || condition.status === 'review_l1') return true;
  if (condition.status === 'resolution_pending') return !condition.resolution?.l1ApprovedAt;
  return false;
}

/** Awaiting THIS viewer as L2: L1-approved items, plus resolutions with L1 sign-off done. */
export function needsL2Action(condition, viewer, orgRouting = null) {
  if (!isL2For(condition, viewer, orgRouting)) return false;
  if (condition.status === 'under_review') return true;
  if (condition.status === 'resolution_pending') return Boolean(condition.resolution?.l1ApprovedAt) && !condition.resolution?.l2ApprovedAt;
  return false;
}

/* ------------------------------------------------------------------ resolution (P2-12) */

/** Who may say "this is fixed": the owner person, anyone in the owner department, the entrant while Pending Review, an org admin. */
export function canResolve(condition, viewer) {
  if (!condition || !viewer) return false;
  if (!['submitted', 'review_l1', 'under_review', 'approved'].includes(condition.status)) return false;
  if (viewer.isAdmin || viewer.role === 'org_admin') return true;
  if (condition.ownerUid && condition.ownerUid === viewer.uid) return true;
  if (!condition.ownerUid && condition.ownerDeptId) {
    if (viewer.deptId === condition.ownerDeptId) return true;
    if (Array.isArray(viewer.memberOfDeptIds) && viewer.memberOfDeptIds.includes(condition.ownerDeptId)) return true;
  }
  if (condition.status === 'submitted' && condition.submittedBy === viewer.uid) return true;
  return false;
}

/** Resolution sign-off: L1 first, then L2 (or straight to Resolved on single-step routing). */
export function canApproveResolution(condition, viewer, orgRouting = null) {
  if (condition?.status !== 'resolution_pending') return null;
  const r = condition.resolution || {};
  if (!r.l1ApprovedAt) return isL1For(condition, viewer, orgRouting) ? 'l1' : null;
  if (!isSingleStep(condition, orgRouting) && !r.l2ApprovedAt) return isL2For(condition, viewer, orgRouting) ? 'l2' : null;
  return null;
}

/** What resolving does from a given status (P2-12): direct close from Pending Review, else the approval chain. */
export function resolutionTarget(status) {
  return status === 'submitted' ? 'resolved' : 'resolution_pending';
}

/* ------------------------------------------------------------------ override / self-correction (P2-36) */

export function canCancel(viewer) {
  return Boolean(viewer && (viewer.isAdmin || viewer.role === 'org_admin'));
}

/**
 * Revise a decision "until the next hop acts": an L1 reviewer may reopen their Not Approved /
 * L1-approved item while it is still at L2; an L2 reviewer may reopen Approved/Active or Not
 * Approved until a resolution is submitted; administrators always.
 */
export function canRevise(condition, viewer, orgRouting = null) {
  if (!condition || !viewer) return false;
  if (viewer.isAdmin || viewer.role === 'org_admin') return ['under_review', 'approved', 'rejected', 'review_l1'].includes(condition.status);
  const l1 = isL1For(condition, viewer, orgRouting);
  const l2 = isL2For(condition, viewer, orgRouting);
  if (condition.status === 'under_review' && l1) return true; // L1 approved, L2 not yet acted
  if (condition.status === 'rejected') return (condition.l2?.rejectedAt ? l2 : l1) || false;
  if (condition.status === 'approved' && l2) return true;
  return false;
}

/** Cleaning house: hide closed items from MY view only (P2-13). */
export function canHide(condition) {
  return Boolean(condition) && ['resolved', 'rejected', 'withdrawn', 'cancelled'].includes(condition.status);
}

/** Sort helper: highest role first (for reviewer pick lists). */
export function byRoleRank(a, b) {
  return roleRank(b.role) - roleRank(a.role) || String(a.displayName || '').localeCompare(String(b.displayName || ''));
}

export { ROLE_HIERARCHY };
